import { Grid, Typography, Alert, Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux'
import { setLocation } from '../store/submission.slice'
import { GeolocateControl } from 'react-map-gl';
import { useNavigate } from "react-router-dom";
import useStateRef from "react-usestateref";
import { appRoutes } from "../AppRoutes";
import AppFooter from "../components/AppFooter";
import AppHeader from "../components/AppHeader";
import BodyContainer from "../components/BodyContainer";
import FooterButtons from "../components/FooterButtons";
import Map from "../components/Map";
import MapGeosearch from '../components/MapGeosearch';
import MapHomeButton from "../components/MapHomeButton";
import MapMarker from '../components/MapMarker';
import { layers } from "../config/map.constants";
import { getFeaturesByLonLat } from "../services/map.service";
import mapConfig from "../config/map.config";
import { getCachedUserLocation } from "../services/location.service";
import { getCardinalDirectionFromAngle, getCategoryDayForLatLng, getLatestWeatherObservationsForLatLng } from "../services/weather.service";
import { burnGuidelinesByCategoryDay } from "../config/burns.constants";
import { setCategoryDay, setWeatherData } from "../store/submission.slice";
import InfoField from "../components/InfoField";
import InfoFieldsContainer from "../components/InfoFieldsContainer";


const SelectLocation = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const isOnline = useSelector(state => state.cache.isOnline)

	const [map, setMap] = useState();
	const [mapLayers, setMapLayers] = useState([]);
	const [sources, setSources] = useState([]);
	const [error, setError] = useState(false);

	const location = useSelector(state => state.submission.location);
	const [selectedLoc, setSelectedLoc, selectedLocRef] = useStateRef(location ? {
		center: [location.coords.longitude, location.coords.latitude],
		properties: {county: location.county }
	} : null); // {center: [lng, lat], properties: any}
	
	const [weatherInfo, setWeatherInfo] = useState({humidity: null, windSpeed: null, windDirection: null});
	const [catDay, setCatDay] = useState(null);


	useEffect(() => {
		if (!map) return;

		const sources = [ layers.arkansas_bound.geojson];
		if (!isOnline) {
			sources.push(layers.arkansas_highways.geojson)
			sources.push(layers.arkansas_city_limits.geojson)
			setSources(sources);

			const cachedLoc = getCachedUserLocation();
			setSelectedLoc({
				properties: {county: cachedLoc.county},
				center:[cachedLoc.center[1], cachedLoc.center[0]]
			});
			saveLocation();

		} else {
			setSources(sources);
			initEventListeners();
		}

	}, [isOnline, map])


	useEffect(() => {
		// if (!mapCenter) return;
		if (!selectedLoc) return;
		const mapCenter = {
			latitude: selectedLoc.center[1],
			longitude: selectedLoc.center[0],
		}

		getCategoryDayForLatLng(mapCenter.latitude, mapCenter.longitude).then(catDay => {
			setCatDay(catDay);
			dispatch(setCategoryDay(catDay));
		});

		getLatestWeatherObservationsForLatLng(mapCenter.latitude, mapCenter.longitude).then(weatherInfo => {
			const humidity = weatherInfo && weatherInfo.relativeHumidity && weatherInfo.relativeHumidity.value ? parseFloat(weatherInfo.relativeHumidity.value) : null;
			const windSpeed = weatherInfo && weatherInfo.windSpeed && weatherInfo.windSpeed.value ? parseFloat(weatherInfo.windSpeed.value) / 3.6 : null;
			const windDirection = weatherInfo && weatherInfo.windDirection && weatherInfo.windDirection.value ? parseFloat(weatherInfo.windDirection.value) : null;

			const weatherData = {
				humidity: { value: humidity ? Math.round(humidity) : 0,  good: humidity > 20},
				windSpeed: { value: windSpeed ? Math.round(windSpeed) : 0, good: windSpeed <= 15},
				windDirection: { value: windDirection, good: true}
			}

			dispatch(setWeatherData(weatherData));
			setWeatherInfo(weatherData);
		});

	}, [selectedLoc])

	const layerClickedRef = useRef(false)
	const initEventListeners = () => {
		if (!map) return;
		map.on('click', layers.arkansas_bound.id, e => {
			setError(false);
			setSelectedLoc({
				center: [e.lngLat.lng, e.lngLat.lat],
				properties: e.features.length ? {
					...e.features[0].properties,
					county: e.features[0].properties.county_nam
				} : null
			});
			saveLocation();
			layerClickedRef.current = true;
		});

		map.on('click', (e) => {
			if (layerClickedRef.current) {
				layerClickedRef.current = false;
				return;
			}
			setError(true); // clicked outside of layer
		})
	}

	const onGeosearchResult = async r => {
		const isInBounds = await isLatLongWithinStateBounds( r.result.center[1], r.result.center[0]);
		if (isInBounds) {
			setSelectedLoc(r.result);
			setError(false);
			saveLocation();
		} else {
			setSelectedLoc(null);
			setError(true); // geosearch result is outside of county
		}
	}

	const onGeolocateResult = async r => {
		const isInBounds = await isLatLongWithinStateBounds(r.coords.latitude, r.coords.longitude);
		if (isInBounds) {
			setError(false);
			setSelectedLoc({
				center: [r.coords.longitude, r.coords.latitude],
				properties: null
			});
			saveLocation();
		} else {
			setSelectedLoc(null);
			setError(true); // geolocate result is outside of county
		}
	}

	const saveLocation = () => {
		setError(false);

		if (selectedLocRef.current) {
			const loc = selectedLocRef.current;
			const county = loc.properties ? loc.properties.county : '';
			const coords = {latitude: loc.center[1], longitude: loc.center[0]};
			dispatch(setLocation({ county, coords }));
		}
	}

	const onNext = () => {
		navigate(appRoutes.FuelLoading);
	}

	const isLatLongWithinStateBounds = async (lat, lng) => {
		try {
			const features = await getFeaturesByLonLat(layers.arkansas_bound.url, {lng, lat}, 100, true, []);
			const countyFeature = features && features.length ? features[0] : null;
			return countyFeature && !!countyFeature.properties.county;
		} catch (e) {
			return false;
		}
	}

	return (
		<>
			<AppHeader />
			<BodyContainer hasFooter={false} hasHeader={true}>
				<Grid item xs={1} mb={4} mt={2} sx={{width: '100% !important'}}>
					<Typography variant="title" component="h1">Select a Prescribed Burn Location</Typography>
					<Typography variant="subtitle2" component="h2">
					Search for location or click a point on the map to set the location of the planned prescribed burn.
					</Typography>
				</Grid>
				<Grid item container xs={9} sx={{width: '100% !important'}}>
					<Map onMapLoad={setMap} mapLayers={mapLayers} sources={sources} center={mapConfig.arkansasCenter} navControls={true}>
						{/* Widgets */}
						{isOnline &&
							<>
							<MapHomeButton />
							<MapGeosearch onResult={onGeosearchResult} />
							<GeolocateControl showUserLocation={false} showAccuracyCircle={false} onGeolocate={onGeolocateResult} />
							</>
						}

						{/* Location selection marker */}
						{selectedLoc && <MapMarker longitude={selectedLoc.center[0]} latitude={selectedLoc.center[1]} />}

						{/* Action buttons */}
						{/* {(isOnline && mode === 'free') && <Button size="small" variant="contained" onClick={() => setMode('selecting')} sx={{ position: 'absolute', bottom: 8, right: 8 }}>Select Location</Button>} */}
						{/* {(isOnline && mode === 'selecting') && <Button size="small" variant="contained" onClick={onSaveLocation} sx={{ position: 'absolute', bottom: 8, right: 8 }}>Save Location</Button>} */}
					</Map>
				</Grid>
				<Grid item xs="auto" mt={1} sx={{width: '100% !important'}}>
					<Typography variant="subtitle" component="h2">Category Day</Typography>
					<Typography variant="text" component="h2">
						Category Day - a scale from 1 to 5 based on transport wind speed and mixing height.
					</Typography>
					<Typography variant="subtitle">
						<Box fontWeight="fontWeightMedium" textAlign="center" mt={3}>
							Category Day in Your Area: 
							<Box 
							color="primary.textBlack"
							ml={2} 
							p={2}
							pb={0}
							pt={1}
							sx={{
								display: 'inline-flex', 
								justifyContent: 'center',
								alignItems: 'center',
								maxWidth: '15px',
								backgroundColor: catDay ? 'white' : 'transparent',
								borderRadius: '4px'
							}}>
								{catDay}
							</Box>
						</Box>
					</Typography>
					<Typography variant="subtitle2" align="center" mt={0}>
						{burnGuidelinesByCategoryDay[catDay-1]}
					</Typography>
				</Grid>
				
				<Grid item xs="auto" mt={3} sx={{width: '100% !important'}}>
					<Typography variant="subtitle" component="h2">Weather Conditions</Typography>
					<InfoFieldsContainer>
						<InfoField 
						title="Wind Direction:" 
						titleProps={{color:"primary.textBlack"}}
						reverse 
						align="center"
						mt={1} 
						mb={1} 
						p={.5} 
						pl={2} 
						sx={{backgroundColor:'white', borderRadius: '4px'}}>
							{weatherInfo.windDirection && weatherInfo.windDirection.value ?
							<Typography variant="text" textAlign="center" color="primary.textBlack" fontWeight="fontWeightBold">{weatherInfo.windDirection.value}° {getCardinalDirectionFromAngle(weatherInfo.windDirection.value)}</Typography>
							:<Typography variant="text" textAlign="center"color="primary.textBlack" fontWeight="fontWeightBold">No current value</Typography>
							}
						</InfoField>

						<InfoField 
						title="Wind Speed:" 
						titleProps={{color:"primary.textBlack"}}
						reverse
						align="center"
						mt={1} 
						mb={1}
						p={.5}
						pl={2}
						sx={{backgroundColor:'white', borderRadius: '4px'}}>
							{weatherInfo.windSpeed && weatherInfo.windSpeed.value ?
							<Typography variant="text" textAlign="center"color="primary.textBlack" fontWeight="fontWeightBold">{weatherInfo.windSpeed.value} MPH</Typography>
							:<Typography variant="text" textAlign="center"color="primary.textBlack" fontWeight="fontWeightBold">No current value</Typography>
							}
						</InfoField>

						<InfoField 
						title="Humidity:"
						titleProps={{color:"primary.textBlack"}}
						reverse
						align="center"
						mt={1}
						p={.5}
						pl={2}
						sx={{backgroundColor:'white', borderRadius: '4px'}}>
							{weatherInfo.humidity &&  weatherInfo.humidity.value ?
							<Typography variant="text" textAlign="center"color="primary.textBlack" fontWeight="fontWeightBold">{weatherInfo.humidity.value} %</Typography>
							:<Typography variant="text" textAlign="center"color="primary.textBlack" fontWeight="fontWeightBold">No current value</Typography>
							}
						</InfoField>
					</InfoFieldsContainer>
				</Grid>
				<Grid item mt={4} sx={{width: '100% !important'}}>
					<FooterButtons backLabel="Go Back" onBack={() => navigate(appRoutes.Home)} onNext={onNext} nextDisabled={!selectedLoc}/>
				</Grid>
			</BodyContainer>
			
			{/* <AppFooter /> */}

			<Alert
				severity="error"
				sx={{ display: error ? 'flex' : 'none', position: 'fixed', zIndex: 4, bottom: 10, width: '90vw', left: 'calc(50% - 45vw)' }}
				onClose={() => setError(false)}>
					You must select a Burn Location within the State of Arkansas. Please try again.
			</Alert>
		</>
	)
}

export default SelectLocation;